import { buildTransferIntelligence } from './mcTransferIntelligence.js';

// DS2.6B is a pure planning calculator. Inputs are operator-reviewed policy and
// the existing canonical DS2 evidence; it never writes or executes a transfer.
const SCALE = 1e6;
const scopeKey = (value) => String(value || '').trim().toLowerCase();
const numeric = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Decimal shifting avoids floating subtraction/pack rounding of source stock.
// Current SOH is conservatively truncated at six decimal places. All preview
// allocations and reserves use integer ticks, so destinations share one budget.
function stockTicks(value) {
  if (!(value >= 0) || !Number.isFinite(value)) return null;
  const [mantissa, exponent = '0'] = String(value).toLowerCase().split('e');
  const [whole, fraction = ''] = mantissa.split('.');
  const digits = whole + fraction;
  const end = whole.length + Number(exponent) + 6;
  const shifted = end <= 0 ? '0' : digits.slice(0, end).padEnd(end, '0');
  const ticks = Number(shifted);
  return Number.isSafeInteger(ticks) ? ticks : null;
}

const exactTicks = (value) => {
  const ticks = stockTicks(value);
  return ticks !== null && ticks / SCALE === value ? ticks : null;
};
const floorToStep = (ticks, step) => ticks - (ticks % step);

function needTicks(value) {
  if (!(value >= 0) || !Number.isFinite(value)) return null;
  const scaled = value * SCALE;
  if (!Number.isSafeInteger(Math.ceil(scaled))) return null;
  // Snap only machine-precision noise at a tick boundary in derived estimates;
  // observation spans/trust gates are still evaluated exactly by DS2.6A.
  const nearest = Math.round(scaled);
  const tolerance = Number.EPSILON * Math.max(1, Math.abs(scaled)) * 4;
  return Math.floor(Math.abs(scaled - nearest) <= tolerance ? nearest : scaled);
}

export function formatTransferQuantity(value, suffix = '') {
  const parsed = numeric(value);
  if (parsed === null) return '—';
  if (parsed > 0 && parsed < 1 / SCALE) return `<0.000001${suffix}`;
  return `${Number(parsed.toFixed(6))}${suffix}`;
}

export function buildTransferQuantityPreview({ rows = [], sourceScope = '', evaluatedAt = '', policy = {}, destinationInputs = {} } = {}) {
  const parsedAt = Date.parse(evaluatedAt || '');
  const at = Number.isFinite(parsedAt) ? new Date(parsedAt).toISOString() : new Date().toISOString();
  // Re-evaluate raw evidence, never trust a caller-supplied rank/confidence.
  const canonicalRows = Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object' && !Array.isArray(row)) : [];
  const evidence = buildTransferIntelligence({ rows: canonicalRows, sourceScope, evaluatedAt: at });
  const controls = policy && typeof policy === 'object' ? policy : {};
  const inputs = destinationInputs && typeof destinationInputs === 'object' ? destinationInputs : {};
  const targetCoverDays = numeric(controls.targetCoverDays);
  const sourceReserve = numeric(controls.sourceReserve);
  const destinationMinimum = numeric(controls.destinationMinimum);
  const transferStep = numeric(controls.transferStep);
  const reserveTicks = exactTicks(sourceReserve);
  const minimumTicks = exactTicks(destinationMinimum);
  const stepTicks = exactTicks(transferStep);
  const sourceSoh = numeric(evidence.source?.soh);
  const sourceTicks = stockTicks(sourceSoh);
  const blockers = [...evidence.blockers];
  const sourceSku = scopeKey(evidence.source?.sku);

  if (!(targetCoverDays > 0)) blockers.push('Enter a positive destination target cover in days.');
  if (!(sourceReserve >= 0) || reserveTicks === null) blockers.push('Enter stock to keep at the source, using up to 6 decimal places.');
  if (!(destinationMinimum >= 0) || minimumTicks === null) blockers.push('Enter the destination minimum stock, using up to 6 decimal places.');
  if (!(transferStep > 0) || stepTicks === null || stepTicks <= 0) blockers.push('Enter a positive transfer increment, using up to 6 decimal places.');
  if (controls.unitConfirmed !== true) blockers.push('Confirm that ranked sites use the same SOH unit for this SKU.');
  if (!sourceSku) blockers.push('A canonical source SKU is required for quantity previews.');
  if (sourceTicks === null) blockers.push('Source SOH is outside the supported quantity range.');
  if (sourceSoh !== null && sourceReserve !== null && sourceReserve > sourceSoh) blockers.push('Stock to keep at the source cannot exceed current SOH.');
  if (!evidence.ranked.length && evidence.sourceEligible) blockers.push('No destination has established evidence for a quantity preview.');

  const held = blockers.length > 0;
  const availableTicks = held ? 0 : Math.max(0, sourceTicks - reserveTicks);
  let remainingTicks = availableTicks;
  let allocatedTicks = 0;
  const previews = evidence.ranked.map((row) => {
    const base = { scope: row.scope, site: row.site, sku: row.sku, rank: row.rank, soh: numeric(row.soh), depletionPerDay: row.depletionPerDay,
      quantity: null, incoming: null, targetStock: null, netNeed: null, roundedNeed: null, projectedCoverDays: null, status: 'HELD', reason: '' };
    if (held) return { ...base, reason: 'Complete the source/evidence and preview-policy requirements.' };
    if (scopeKey(row.sku) !== sourceSku) return { ...base, reason: 'Destination SKU does not match the source SKU.' };
    if (!(base.soh > 0) || stockTicks(base.soh) === null) return { ...base, reason: 'Positive numeric destination SOH within the supported quantity range is required.' };
    const incoming = numeric(inputs[row.scope]?.incoming);
    if (!(incoming >= 0) || exactTicks(incoming) === null) {
      return { ...base, reason: 'Confirm planned incoming stock for this site; enter 0 if none is planned.' };
    }
    const targetStock = Math.max(row.depletionPerDay * targetCoverDays, destinationMinimum);
    const netNeed = Math.max(0, targetStock - base.soh - incoming);
    const ticks = needTicks(netNeed);
    if (!Number.isFinite(targetStock) || ticks === null) {
      return { ...base, incoming, reason: 'Target stock/need is outside the supported quantity range.' };
    }
    const roundedTicks = floorToStep(ticks, stepTicks);
    const quantityTicks = Math.min(roundedTicks, floorToStep(remainingTicks, stepTicks));
    remainingTicks -= quantityTicks;
    allocatedTicks += quantityTicks;
    const quantity = quantityTicks / SCALE;
    const projectedCoverDays = (base.soh + incoming + quantity) / row.depletionPerDay;
    if (!Number.isFinite(projectedCoverDays)) {
      // A rate may be too tiny for a usable cover estimate. Do not consume the
      // shared source budget or leave a numeric quantity on that row.
      remainingTicks += quantityTicks;
      allocatedTicks -= quantityTicks;
      return { ...base, incoming, reason: 'Projected cover cannot be estimated from this depletion rate.' };
    }
    let status = 'READY';
    let reason = 'Preview fits the target, transfer increment and remaining source stock.';
    if (netNeed === 0) { status = 'COVERED'; reason = 'Current SOH plus confirmed incoming stock already meets the target.'; }
    else if (roundedTicks === 0) { status = 'BELOW_INCREMENT'; reason = 'Need is smaller than one transfer increment; rounded down to 0.'; }
    else if (quantityTicks === 0) { status = 'SOURCE_LIMIT'; reason = 'No transferable source stock remains after the reserve and earlier destinations.'; }
    else if (quantityTicks < roundedTicks) { status = 'PARTIAL'; reason = 'Source stock limits this preview to a partial allocation.'; }
    return { ...base, incoming, targetStock, netNeed, roundedNeed: roundedTicks / SCALE, quantity, projectedCoverDays, status, reason };
  });
  const totalQuantity = held ? null : allocatedTicks / SCALE;
  return {
    status: held ? 'HELD' : 'PREVIEW', evaluatedAt: at, blockers,
    policy: { targetCoverDays, sourceReserve, destinationMinimum, transferStep, unitConfirmed: controls.unitConfirmed === true },
    source: evidence.source, sourceSoh,
    availableQuantity: held ? null : availableTicks / SCALE,
    transferableQuantity: held ? null : floorToStep(availableTicks, stepTicks) / SCALE,
    totalQuantity, sourceRemaining: held ? null : (sourceTicks - allocatedTicks) / SCALE,
    unusedQuantity: held ? null : remainingTicks / SCALE,
    destinations: previews,
    heldDestinations: previews.filter((row) => row.status === 'HELD').length,
    developingSites: evidence.developing.map((row) => row.site),
    excludedSites: evidence.excluded.map((row) => row.site),
    roundingRule: 'Round down to the transfer increment; a small target shortfall may remain.',
    allocationRule: 'Use DS2.6A rank order and one shared source budget. Unknown incoming stock holds only that destination.',
    executionEnabled: false,
  };
}
